import { ChevLeftFilledIcon, CloseIcon, UseFilterIcon } from '@/components/Icons';
import { useGeneratorFilterStore } from '@/state/generator-filter-store';
import { cn } from '@/utils';
import { useState } from 'react';
import { Tabs, TabsList } from '@/components/ui/tabs';
import { TabsTrigger } from '@radix-ui/react-tabs';
import { GradientButton } from '../../AudioEditor/EditorButton';
import { SfHighIcon, SfLowIcon } from '../StyleFilterList';
import { FilterGroup } from '@/service/type/Filter';
import { getFilterBalance } from '../../../utils/getFilterBalance';

interface StyleFilterDetailProps {
  defaultCurrentFilterGroup?: FilterGroup;
  onSelect: ({ filter, weight }: { filter: FilterGroup; weight: number }) => void;
  filter: FilterGroup;
  onBack: () => void;
  onRemove: () => void;
  filterWeight?: number;
}

const StyleFilterDetail: React.FC<StyleFilterDetailProps> = ({ onSelect, filter, onBack, onRemove, filterWeight }) => {
  const { currentFilterGroup, currentStrength, setCurrentStrength } = useGeneratorFilterStore();
  const balanceData = getFilterBalance({ filter, currentStrength, isSelected: true });
  const balance = balanceData.balance;

  const [weight, setWeight] = useState(filterWeight);

  return (
    <div className="flex flex-col gap-[4px] h-full">
      <div className="w-full h-full border-light-top rounded">
        <div
          className="w-full h-full rounded"
          style={{
            border: '3px solid var(--_Lil-Bottom-Highlight, rgba(255, 255, 255, 0.00))',
            background: 'linear-gradient(180deg,#333537, #2B2E30)',
            backgroundOrigin: 'border-box',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="w-full h-full bg-[#191a1b] rounded-sm flex flex-col">
            <div className="p-[16px]">
              <div
                className="flex gap-[6px] text-[#fff]"
                onClick={() => {
                  onBack();
                }}
              >
                <ChevLeftFilledIcon fillColor="currentColor" />
                <div>BACK</div>
              </div>
            </div>
            <div className="border-b border-[#48484b]"></div>
            <div className={cn('w-full h-full bg-[#131415] text-white px-[16px] pt-[24px] font-pressura-mono')}>
              <div className="flex gap-[16px]">
                <div className=" relative shrink-0">
                  {(filter?.thumb_album_cover ?? filter?.album_cover) ? (
                    <img
                      src={filter?.thumb_album_cover ?? filter?.album_cover}
                      className={cn('block w-[60px] h-[91px]  rounded object-cover bg-cover flex-shrink-0 bg-neutral-800')}
                    />
                  ) : (
                    <div className={cn('block w-[60px] h-[91px]  rounded object-cover bg-cover flex-shrink-0 bg-jen-orange')} />
                  )}

                  {weight === 0 ? (
                    <SfLowIcon className="w-[44px] h-[44px] absolute left-[8px] bottom-[0px]" />
                  ) : (
                    <SfHighIcon className="w-[44px] h-[44px] absolute left-[8px] bottom-[0px]" />
                  )}
                </div>
                <div className="flex flex-col leading-none font-pressura-mono text-[#e0e4e4] text-[12px] gap-[11px] justify-center">
                  <div className="tracking-[-0.36px] uppercase">{filter.artist}</div>
                  <div className="text-[16px] tracking-[-0.48px] uppercase">{filter.name}</div>
                  <div>
                    {balance >= 0 ? `${balance} mins` : 'unlimited mins'}{' '}
                    <span className=" opacity-50 tracking-[-0.36px]">MINS REMAINING</span>
                  </div>
                </div>
              </div>
              <div className="mt-[16px] text-[#e0e4e4] text-[12px] ">FILTER STRENGTH</div>
              <Tabs
                defaultValue={String(filterWeight)}
                onValueChange={(val) => {
                  setWeight(Number(val));
                  setCurrentStrength(Number(val));
                }}
                className="w-full"
              >
                <TabsList className="w-full bg-black text-jen-size-heading-02 mt-jen-spacing-12">
                  <TabsTrigger
                    value="0"
                    className="w-[50%] py-2 px-4 text-sm font-medium rounded-lg text-white bg-black transition duration-200 data-[state=active]:bg-white data-[state=active]:text-black  data-[state=active]:border data-[state=active]:border-black data-[state=inactive]:text-[#9D9DA6] text-[14px] h-[42px]"
                  >
                    LOW
                  </TabsTrigger>
                  {/* <TabsTrigger
                    value="0.5"
                    className="w-[33%] py-2 px-4 text-sm font-medium rounded-lg text-white bg-black   transition duration-200 data-[state=active]:bg-white data-[state=active]:text-black  data-[state=active]:border data-[state=active]:border-black data-[state=inactive]:text-[#9D9DA6] text-[14px] h-[42px]"
                  >
                    MID
                  </TabsTrigger> */}
                  <TabsTrigger
                    value="1"
                    className="w-[50%] py-2 px-4 text-sm font-medium rounded-lg text-white bg-black transition duration-200 data-[state=active]:bg-white data-[state=active]:text-black  data-[state=active]:border data-[state=active]:border-black data-[state=inactive]:text-[#9D9DA6] text-[14px] h-[42px]"
                  >
                    HIGH
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full h-[72px] flex justify-center items-center">
        {filter.id === currentFilterGroup?.id && (
          <GradientButton
            type="button"
            className="text-[#111112] hover:bg-orange-600 border border-[#111112] radius-xs"
            onClick={() => {
              onRemove?.();
            }}
            text={<span className="text-[#efeff0] text-[12px]">REMOVE</span>}
            icon={<CloseIcon fillColor="#efeff0" />}
            width="40%"
          />
        )}

        <GradientButton
          type="button"
          className={cn('hover:bg-gradient-to-br hover:from-[#ff5b27] hover:to-[#c24017]  radius-xs ml-1', {
            'ml-0': filter.id !== currentFilterGroup?.id,
          })}
          onClick={() => {
            onSelect({ filter, weight: Number(weight) });
          }}
          text={<span className="text-[#efeff0] text-[12px]">USE FILTER</span>}
          width={filter.id === currentFilterGroup?.id ? '76%' : '100%'}
          icon={<UseFilterIcon fillColor="#efeff0" />}
        />
      </div>
    </div>
  );
};

export default StyleFilterDetail;
